/* eslint-disable no-restricted-properties */

import { logRuntimeError } from 'Diagnostics/runtimeLogger';
import type { PlatformHelpers, StoragePermissions } from 'Models/uiConfig/platformHelpers';
import type { RuntimeConfigValue } from 'Models/uiConfig/requestTypes';

import { getShopPayText as buildShopPayText, type ShopPayTerms } from './shopPay';

/**
 * Payload shape for `window.Shopify.analytics.publish(eventName, data)`.
 * Shopify Web Pixels accept any JSON-serializable bag of values; we model that with
 * `RuntimeConfigValue` so the constraint matches the rest of the runtime-config surface.
 * If a future cross-platform analytics-event type emerges, fold this in.
 */
type WebPixelEventPayload = Record<string, RuntimeConfigValue>;

enum ShopifyPageType {
  Home = 'home',
  Product = 'product',
  Collection = 'collection',
  Collections = 'collections',
  Vendors = 'vendors',
  Page = 'page',
  Cart = 'cart',
}

export function publishWebpixelEvent(eventName: string, data: WebPixelEventPayload): void {
  try {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, data);
    }
  } catch (error) {
    // The Shopify Web Pixels surface is sandbox-loaded and can throw at any
    // time (script not yet ready, sandbox revoked, merchant override).
    // Swallowing the throw is intentional, but a silent catch made
    // misconfiguration invisible — surface the failure instead.
    logRuntimeError({
      source: 'shopify:publishWebpixelEvent',
      error,
      extra: { 'event.name': eventName },
    });
  }
}

export function getPageType(): ShopifyPageType | undefined {
  const convermaxPage = window.Convermax.Shopify?.pageType;
  if (convermaxPage) {
    switch (convermaxPage) {
      case 'index':
        return ShopifyPageType.Home;
      case 'collection':
        return window.Convermax.Shopify?.collectionVendor
          ? // vendor collection page (/collections/vendors?q=)
            ShopifyPageType.Vendors
          : ShopifyPageType.Collection;
      case 'list-collections':
        return ShopifyPageType.Collections;
      default:
        return toKnownPageType(convermaxPage);
    }
  }

  return toKnownPageType(window.ShopifyAnalytics?.meta?.page?.pageType);
}

function toKnownPageType(value: string | undefined): ShopifyPageType | undefined {
  if (!value) {
    return undefined;
  }
  const known = (Object.values(ShopifyPageType) as string[]).includes(value);
  return known ? (value as ShopifyPageType) : undefined;
}

/**
 * Several merchant themes inject Shopify globals as strings rather than
 * numbers (e.g. `meta.product.id = "1234"`). The static type says
 * `number | undefined`, but at runtime we must coerce or the downstream
 * `===` comparisons silently mismatch. Returns `undefined` for empty
 * strings, `NaN`, and non-finite values.
 */
function coerceId(value: number | string | undefined): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function getProductId(): number | undefined {
  return coerceId(window.Convermax.Shopify?.productId ?? window.ShopifyAnalytics?.meta?.product?.id);
}

export function getProductType(): string | undefined {
  return window.Convermax.Shopify?.productType ?? window.ShopifyAnalytics?.meta?.product?.type;
}

export function getVariantId(): number | undefined {
  // always get the updated variant id on a variant selection
  const urlVariantId = new URLSearchParams(window.location.search).get('variant');
  return (
    coerceId(urlVariantId ?? undefined) ??
    coerceId(
      window.Convermax.Shopify?.selectedVariantId ??
        window.ShopifyAnalytics?.meta?.product?.variants?.[0]?.id,
    )
  );
}

export function getCollectionId(): number | undefined {
  return coerceId(
    window.Convermax.Shopify?.collectionId ??
      (getPageType() === ShopifyPageType.Collection
        ? window.ShopifyAnalytics?.meta?.page?.resourceId
        : undefined),
  );
}

export function getPageId(): number | undefined {
  return coerceId(
    window.Convermax.Shopify?.pageId ??
      (getPageType() === ShopifyPageType.Page ? window.ShopifyAnalytics?.meta?.page?.resourceId : undefined),
  );
}

export function getCustomerId(): number | undefined {
  return coerceId(
    window.Convermax.Shopify?.customerId ??
      window.ShopifyAnalytics?.meta?.page?.customerId ??
      window.meta?.page?.customerId ??
      window.__st?.cid,
  );
}

export function getCustomerTags(): string[] | undefined {
  return window.Convermax.Shopify?.customerTags;
}

export function getCompanyId(): number | undefined {
  return window.Convermax.Shopify?.companyId;
}

export function getCompanyLocationId(): number | undefined {
  return window.Convermax.Shopify?.companyLocationId;
}

export function getCountry(): string | undefined {
  return window.Convermax.Shopify?.country ?? window.Shopify?.country;
}

export function getLocale(): string | undefined {
  return window.Convermax.Shopify?.locale ?? window.Shopify?.locale;
}

export function getCurrency(): string | undefined {
  return window.Convermax.Shopify?.currency ?? window.Shopify?.currency?.active;
}
export function getCurrencyRate(): string | undefined {
  // there is no way to obtain a rate using liquid
  return window.Shopify?.currency?.rate;
}

export function getRootRoute(): string {
  return (window.Convermax.Shopify?.rootRoute ?? window.Shopify?.routes?.root ?? '').replace(/\/+$/, '');
}

type CartUrls = {
  root?: string;
  add?: string;
  change?: string;
  clear?: string;
  update?: string;
};

export function getCartUrls(): CartUrls {
  const cmShopify = window.Convermax.Shopify;
  const rootRoute = getRootRoute();

  return {
    root: cmShopify?.cartUrl ?? `${rootRoute}/cart`,
    add: cmShopify?.cartAddUrl ?? `${rootRoute}/cart/add`,
    change: cmShopify?.cartChangeUrl ?? `${rootRoute}/cart/change`,
    clear: cmShopify?.cartClearUrl ?? `${rootRoute}/cart/clear`,
    update: cmShopify?.cartUpdateUrl ?? `${rootRoute}/cart/update`,
  };
}

export function getSearchPageUrl(): string {
  return window.Convermax.Shopify?.searchPageUrl ?? `${getRootRoute()}/pages/search`;
}

export function getCollectionsPageUrl(): string {
  return window.Convermax.Shopify?.collectionsUrl ?? `${getRootRoute()}/collections`;
}

/**
 * Optional-string narrowing helper for theme metadata fields. Anything
 * that isn't a non-empty string is dropped — `Object.assign` later
 * skips `undefined` properties, so callers see a clean shape.
 */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export function getThemeInfo(): ThemeInfo | undefined {
  const theme = window.Shopify?.theme;
  if (!theme || typeof theme !== 'object') {
    return undefined;
  }
  // ThemeInfo's fields are all optional, but the wire format is at the
  // mercy of the storefront `<script>` block. Validate each field we
  // ever read so a string `id` (some merchants ship `"123"`) doesn't
  // mismatch a number-typed `getTrackedTheme()` comparison, and a
  // numeric `name` doesn't crash a `name.toLowerCase()` consumer.
  const raw = theme as Record<string, unknown>;
  const result: ThemeInfo = {};
  const id = coerceId(raw.id as number | string | undefined);
  if (id !== undefined) {
    result.id = id;
  }
  const name = optionalString(raw.name);
  if (name !== undefined) {
    result.name = name;
  }
  const role = optionalString(raw.role);
  if (role !== undefined) {
    result.role = role;
  }
  const schemaName = optionalString(raw.schema_name);
  if (schemaName !== undefined) {
    // eslint-disable-next-line camelcase
    result.schema_name = schemaName;
  }
  const schemaVersion = optionalString(raw.schema_version);
  if (schemaVersion !== undefined) {
    // eslint-disable-next-line camelcase
    result.schema_version = schemaVersion;
  }
  return result;
}

export function getShopPayText(
  price: number,
  variantId: number | string | undefined,
  available: boolean,
  shopPayTerms?: ShopPayTerms,
): string | undefined {
  return buildShopPayText(price, variantId, available, shopPayTerms, getCountry(), getCurrency());
}

export const isInDesignMode = !!window.Convermax.Shopify?.isInDesignMode;

export type ConvermaxShopifyGlobal = {
  cartUrl?: string;
  cartAddUrl?: string;
  cartChangeUrl?: string;
  cartClearUrl?: string;
  cartUpdateUrl?: string;
  collectionId?: number;
  collectionsUrl?: string;
  collectionVendor?: string;
  country?: string;
  currency?: string;
  currencyRate?: string;
  customerId?: number;
  customerTags?: string[];
  companyId?: number;
  companyLocationId?: number;
  isInDesignMode?: boolean;
  locale?: string;
  pageType?: string;
  pageId?: number;
  productId?: number;
  productType?: string;
  rootRoute?: string;
  searchPageUrl?: string;
  selectedVariantId?: number;
} & ConvermaxShopifyHelpers;

// Kept hand-maintained because deriving this via `typeof shopifyHelpers` would
// pull the runtime closure — which transitively reads `window.Convermax.Shopify`
// back through `ConvermaxGlobal` — into the type graph and trigger a self-
// reference error on `ConvermaxShopifyGlobal`. Add/remove entries here when the
// helper set changes.
type ConvermaxShopifyHelpers = {
  getCartUrls: () => CartUrls;
  getCollectionId: () => number | undefined;
  getCollectionsPageUrl: () => string;
  getCountry: () => string | undefined;
  getCurrency: () => string | undefined;
  getCustomerId: () => number | undefined;
  getCustomerTags: () => string[] | undefined;
  getCompanyId: () => number | undefined;
  getCompanyLocationId: () => number | undefined;
  isInDesignMode: boolean;
  getLocale: () => string | undefined;
  getPageId: () => number | undefined;
  getPageType: () => ShopifyPageType | undefined;
  getProductId: () => number | undefined;
  getProductType: () => string | undefined;
  getRootRoute: () => string | undefined;
  getSearchPageUrl: () => string;
  getShopPayText: (
    price: number,
    variantId: number | string | undefined,
    available: boolean,
    terms?: ShopPayTerms,
  ) => string | undefined;
  getThemeInfo: () => ThemeInfo | undefined;
  getVariantId: () => number | undefined;
  publishWebpixelEvent: (eventName: string, data: WebPixelEventPayload) => void;
};

export type ThemeInfo = {
  id?: number;
  name?: string;
  role?: string;
  schema_name?: string;
  schema_version?: string;
};

export type Shopify = {
  loadFeatures?: ((
    features: Array<{ name: string; version: string }>,
    callback: (error?: unknown) => void,
  ) => void) & { q?: unknown[] };
  customerPrivacy?: {
    analyticsProcessingAllowed: () => boolean;
    preferencesProcessingAllowed: () => boolean;
  };
  country?: string;
  currency?: {
    active?: string;
    rate?: string;
  };
  locale?: string;
  routes?: {
    root?: string;
  };
  shop?: string;
  theme?: ThemeInfo;
  analytics?: {
    publish?: (eventName: string, data: WebPixelEventPayload) => void;
  };
};

export type ShopifyAnalytics = {
  meta?: {
    page?: {
      pageType?: string;
      resourceId?: number;
      customerId?: number;
    };
    product?: {
      id?: number;
      type?: string;
      variants?: Array<{ id?: number }>;
    };
  };
};

export type ShopifyMeta = {
  page?: {
    customerId?: number;
  };
};

export type ShopifyTools = {
  cid?: number;
};

const shopifyHelpers: ConvermaxShopifyHelpers = {
  getCartUrls,
  getCollectionsPageUrl,
  getCollectionId,
  getCompanyId,
  getCompanyLocationId,
  getCountry,
  getCurrency,
  getCustomerId,
  getCustomerTags,
  isInDesignMode,
  getLocale,
  getPageId,
  getPageType,
  getProductId,
  getProductType,
  getRootRoute,
  getSearchPageUrl,
  getShopPayText,
  getThemeInfo,
  getVariantId,
  publishWebpixelEvent,
};

export function getStoragePermissions(): StoragePermissions {
  try {
    const privacy = window.Shopify?.customerPrivacy;
    return {
      analytics: privacy?.analyticsProcessingAllowed() ?? null,
      functional: privacy ? privacy.preferencesProcessingAllowed() : true,
    };
  } catch {
    return { analytics: null, functional: null };
  }
}

export function initializePlatformHelpers(): void {
  window.Convermax.Shopify = {
    ...(window.Convermax.Shopify || {}),
    ...shopifyHelpers,
  };
}

export function initializePlatformStorage(onStoragePermissionsChanged: () => void): Promise<void> {
  window.document.addEventListener('visitorConsentCollected', onStoragePermissionsChanged);

  return new Promise<void>((resolve) => {
    const onPrivacyLoaded = (error?: unknown) => {
      if (error) {
        logRuntimeError({ source: 'shopify:storage_permissions', error });
      } else {
        onStoragePermissionsChanged();
      }
      resolve();
    };

    if (!window.Shopify?.loadFeatures) {
      resolve();
      return;
    }

    const loadPrivacyApi = (version: string, callback: (error?: unknown) => void) => {
      try {
        window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version }], callback);
      } catch (error) {
        onPrivacyLoaded(error);
      }
    };

    loadPrivacyApi('0.2', async (error) => {
      if (error) {
        // Shopify replaces its queued loader after these callbacks return.
        await Promise.resolve();
        if (window.Shopify?.loadFeatures) {
          loadPrivacyApi('0.1', onPrivacyLoaded);
        } else {
          onPrivacyLoaded(error);
        }
        return;
      }
      onPrivacyLoaded();
    });

    // Shopify's deferred loader drains this queue before DOMContentLoaded unless a theme lazy-loads it.
    const resolveIfLoaderIsQueued = () => {
      if (Array.isArray(window.Shopify?.loadFeatures?.q)) {
        resolve();
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolveIfLoaderIsQueued, { once: true });
    } else {
      resolveIfLoaderIsQueued();
    }
  });
}

const platformHelpers = {
  initializePlatformHelpers,
  initializePlatformStorage,
  getStoragePermissions,
} satisfies Partial<PlatformHelpers>;
export default platformHelpers;
